import { IApiAxiosError } from "@/api";
import { VariantType } from "notistack";

export interface AuthError {
    snackBarVariant: VariantType;
    message: string;
}

/**
 * Parses authentication-related API errors and returns a standardized error object
 * @param error - The error object from the API request
 * @returns An object containing the error type and user-friendly message
 */
export function parseAuthError(error: unknown): AuthError {
    const axiosError = error as IApiAxiosError;
    const status = axiosError.response?.status;
    const detail =
        axiosError.response?.data?.detail ||
        "An error occurred. Please try again.";

    const detailLower = detail.toLowerCase();

    // Handle 400 Bad Request errors
    if (status === 400) {
        // Invalid/expired token
        if (detailLower.includes("invalid") && detailLower.includes("token")) {
            return {
                snackBarVariant: "error",
                message:
                    "This password reset link is invalid or has expired. Please request a new one.",
            };
        }

        // Password length validation
        if (
            detailLower.includes("at least") ||
            detailLower.includes("characters long")
        ) {
            return {
                snackBarVariant: "error",
                message: detail,
            };
        }

        // Password strength validation
        if (detailLower.includes("too weak")) {
            return {
                snackBarVariant: "error",
                message: detail,
            };
        }

        // Password history validation
        if (detailLower.includes("recently used")) {
            return {
                snackBarVariant: "error",
                message: detail,
            };
        }

        // Invitation already accepted
        if (detailLower.includes("already been accepted")) {
            return {
                snackBarVariant: "error",
                message:
                    "This invitation has already been used. Please contact your administrator if you need a new invitation.",
            };
        }

        // Invalid credentials (login)
        return {
            snackBarVariant: "error",
            message: detail,
        };
    }

    // Handle 403 Forbidden errors
    if (status === 403) {
        // Account lockout (detected by message content)
        if (
            detailLower.includes("locked") ||
            detailLower.includes("too many")
        ) {
            return {
                snackBarVariant: "warning",
                message: detail,
            };
        }

        // Disabled account
        return {
            snackBarVariant: "error",
            message:
                "Your account has been disabled. Please contact support for assistance.",
        };
    }

    // Handle 404 Not Found errors
    if (status === 404) {
        return {
            snackBarVariant: "error",
            message:
                "User account not found. Please contact support for assistance.",
        };
    }

    // Handle 429 Rate Limit errors
    if (status === 429) {
        return {
            snackBarVariant: "error",
            message: detail,
        };
    }

    // Fallback for unexpected errors
    return {
        snackBarVariant: "error",
        message: detail,
    };
}
